import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';

@Component({
  selector: 'th[lib-sticky-table-column], td[lib-sticky-table-column]',
  templateUrl: './sticky-table-column.component.html',
  styleUrls: ['./sticky-table-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class StickyTableColumnComponent implements OnInit, OnDestroy {
  @Input() width: string = '86px';
  @Input() tableWrapperSelector: string = '.table-wrapper';
  @Input() hideThreshold: number = 0.9;

  private scrollListener?: () => void;
  private hideShadow: boolean = false;

  @HostBinding('class') get cssClass() {
    const baseClass = 'sticky-action';
    const typeClass = this.elementRef.nativeElement.tagName.toLowerCase() === 'th' ? 'th' : 'td';
    return `${baseClass} ${typeClass}`;
  }

  @HostBinding('style.width') get styleWidth() {
    return this.width;
  }

  @HostBinding('style.min-width') get styleMinWidth() {
    return this.width;
  }

  @HostBinding('style.--left-shadow-opacity') get shadowOpacity() {
    return this.hideShadow ? '0' : '1';
  }

  @HostBinding('style.--left-shadow-visibility') get shadowVisibility() {
    return this.hideShadow ? 'hidden' : 'visible';
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.setupScrollListener();
  }

  ngOnDestroy() {
    if (this.scrollListener) {
      this.scrollListener();
    }
  }

  private setupScrollListener(): void {
    // Try multiple possible selectors for the scrollable container
    const possibleSelectors = [
      this.tableWrapperSelector, // .table-wrapper (disbursement history)
      '.table-container', // app-data-table-v2 wrapper
      'app-data-table-v2', // the component itself
      '.w-full', // table inside app-data-table-v2
    ];

    let tableWrapper: Element | null = null;

    for (const selector of possibleSelectors) {
      tableWrapper = this.elementRef.nativeElement.closest(selector);
      if (tableWrapper) {
        break;
      }
    }

    if (tableWrapper) {
      // For app-data-table-v2, we need to find the actual scrollable container
      if (tableWrapper.tagName === 'APP-DATA-TABLE-V2') {
        const tableContainer = tableWrapper.querySelector('.table-container');
        if (tableContainer) {
          tableWrapper = tableContainer;
        }
      }

      this.scrollListener = this.renderer.listen(tableWrapper, 'scroll', (event: Event) => {
        this.onTableScroll(event);
      });
    }
  }

  private onTableScroll(event: Event): void {
    const scrollContainer = event.target as HTMLElement;
    const scrollLeft = scrollContainer.scrollLeft;
    const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;

    // Hide shadow when scrolled near the right edge
    const shouldHideShadow = scrollLeft >= maxScrollLeft * this.hideThreshold;

    this.hideShadow = shouldHideShadow;
  }
}
